import { useEffect, useRef, useState } from 'react';
import { colors, fonts } from '../theme';
import { dmpSteps } from './dmpSteps';

const compareCols = [
  {
    label: 'Human expert assessment',
    title: "Subjective, and it doesn't travel",
    items: [
      'Manual cross-check, per shipment, per buyer',
      'Inconsistent across reviewers',
      'Opaque — folders of static PDFs',
    ],
  },
  {
    label: 'AI assessment alone',
    title: 'Fast, but not provable',
    items: [
      'Confident answers, not defensible verdicts',
      'Same evidence, two runs, two answers',
      "A probability can't sit in an audit trail",
    ],
  },
  {
    label: 'Deterministic execution layer',
    title: "Explicit, and it's verifiable",
    answer: true,
    items: [
      'Specs compiled into machine-verifiable logic — expert-vetted',
      'An execution trace — every rule, every input, replayable',
      'Buyers verify the proof themselves — no re-assessment',
    ],
  },
];

const StepsTeaser = () => {
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setVisible(true); });
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={containerRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 16 }}>
      {dmpSteps.map((step, i) => (
        <div key={step.num} style={{
          background: colors.bgAlt,
          border: `1px solid ${colors.border}`,
          borderRadius: 12,
          padding: '20px 18px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(14px)',
          transition: `opacity .5s ease ${i * 0.1}s, transform .5s ease ${i * 0.1}s`,
        }}>
          <div style={{ fontFamily: fonts.mono, fontSize: 12, color: colors.teal, fontWeight: 700, marginBottom: 8 }}>{step.num}</div>
          <div style={{ color: colors.navy, fontSize: 15.5, fontWeight: 700, marginBottom: 6 }}>
            {step.title}
            {step.optional && (
              <span style={{ marginLeft: 8, fontFamily: fonts.mono, fontSize: 9, color: colors.slate, border: `1px solid ${colors.border}`, borderRadius: 20, padding: '2px 7px', fontWeight: 600, verticalAlign: 'middle' }}>OPTIONAL</span>
            )}
          </div>
          <p style={{ color: colors.slate, fontSize: 13, lineHeight: 1.6 }}>{step.blurb}</p>
        </div>
      ))}
    </div>
  );
};

const HowItWorks = () => (
  <section style={{ padding: 'clamp(56px, 8vw, 96px) clamp(20px, 5vw, 48px)', background: colors.bg }}>
    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
      <div style={{ maxWidth: 760, marginBottom: 48 }}>
        <p style={{ color: colors.teal, fontSize: 12.5, fontFamily: fonts.mono, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 16 }}>The execution layer</p>
        <h2 style={{ fontSize: 'clamp(26px,3.4vw,38px)', fontFamily: fonts.display, lineHeight: 1.3, color: colors.navy, marginBottom: 14 }}>
          Material conformance needs a deterministic execution layer — not an assessment or a statistical guess
        </h2>
        <p style={{ color: colors.slateDark, fontSize: 16.5, lineHeight: 1.65, maxWidth: 640 }}>
          An AI model returns a confident answer, but it's a probabilistic one. A compliance gate needs a defensible verdict. Even a slight variation will have downstream effects — and neither human review nor AI alone produces something a buyer can independently check.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 20 }}>
        {compareCols.map((c, i) => (
          <div key={i} style={{
            background: c.answer ? colors.navy : colors.bg,
            border: `1px solid ${c.answer ? colors.navy : colors.border}`,
            borderRadius: 14,
            padding: '28px 26px',
          }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: c.answer ? colors.teal : colors.slate, marginBottom: 14 }}>{c.label}</div>
            <div style={{ color: c.answer ? '#fff' : colors.navy, fontSize: 19, fontWeight: 700, marginBottom: 14 }}>{c.title}</div>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {c.items.map((item, j) => (
                <li key={j} style={{ fontSize: 14.5, color: c.answer ? 'rgba(255,255,255,0.78)' : colors.slate, paddingLeft: 16, position: 'relative', lineHeight: 1.6 }}>
                  <span style={{ position: 'absolute', left: 0, color: c.answer ? colors.teal : colors.slate }}>{c.answer ? '✓' : '–'}</span>{item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div id="how" style={{ maxWidth: 760, marginTop: 72, marginBottom: 36, scrollMarginTop: 84 }}>
        <p style={{ color: colors.teal, fontSize: 12.5, fontFamily: fonts.mono, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 16 }}>How Digital Material Passport works</p>
        <h2 style={{ fontSize: 'clamp(26px,3.4vw,38px)', fontFamily: fonts.display, lineHeight: 1.3, color: colors.navy, marginBottom: 14 }}>
          From requirement to proof, in four steps
        </h2>
        <p style={{ color: colors.slateDark, fontSize: 16.5, lineHeight: 1.65, maxWidth: 640 }}>
          The execution layer turns your existing systems — ERP, MES, QMS, LIMS — into a continuous, auditable chain of evidence, without asking your team to change how they work.
        </p>
      </div>

      <StepsTeaser />

      <div style={{ marginTop: 32, textAlign: 'center' }}>
        <a href="/digital-material-passport" className="ghost"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: 10, padding: '13px 26px', color: colors.navy, fontSize: 14.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
          Read more →
        </a>
      </div>
    </div>
  </section>
);

export default HowItWorks;
